/**
 * Portal Security (ADR-0016) — the single home for centralized access.
 *
 * Now that the portal is the only registration door, this is where an operator
 * governs who gets in: the registration policy (open + direct|approval) and the
 * registrations queue (pending self-signups → approve/reject). Approving runs
 * the backend activation hook (activate + auto-enroll). The per-app default-role
 * + auto-enroll matrix lands alongside in a follow-up.
 *
 * Operator console (Apps plane) — gated by the `parent_portal` / `apps` RBAC.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AppWindow, Check, Loader2, ShieldCheck } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/ui/button";
import { EmptyState, PageContainer, PageContent, PageHeader } from "@/components/ui/page";
import { Switch } from "@/components/ui/switch";
import { appsApi, type AppSummary } from "@/lib/api-apps";
import { parentPortalApi, type Registration, type Security } from "@/lib/api-parent-portal";

/** A label/help on the left + a control on the right (mirrors ParentPortalPage). */
function Row({
  title,
  help,
  children,
}: {
  title: string;
  help?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex items-center justify-between gap-4 py-3">
      <div className="min-w-0">
        <div className="text-sm font-medium text-foreground">{title}</div>
        {help && <div className="mt-0.5 text-xs text-muted-foreground">{help}</div>}
      </div>
      <div className="shrink-0">{children}</div>
    </div>
  );
}

/** One app's access defaults: the role pre-selected at assignment + the
 *  auto-enroll flag (ADR-0016). Auto-enroll requires a default role, so the
 *  switch is disabled until one is picked. */
function AppAccessRow({ app }: { app: AppSummary }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const detailQ = useQuery({
    queryKey: ["apps", app.id, "detail"],
    queryFn: () => appsApi.get(app.id),
  });
  const rolesQ = useQuery({
    queryKey: ["apps", app.id, "roles"],
    queryFn: () => appsApi.listRoles(app.id),
  });
  const save = useMutation({
    mutationFn: (body: { default_role_id?: string | null; auto_enroll?: boolean }) =>
      appsApi.update(app.id, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["apps", app.id, "detail"] }),
  });

  const detail = detailQ.data;
  const roles = rolesQ.data ?? [];
  const defaultRoleId = detail?.default_role_id ?? "";
  const autoEnroll = detail?.auto_enroll ?? false;

  return (
    <div className="flex items-center justify-between gap-4 py-3">
      <div className="min-w-0">
        <div className="truncate text-sm font-medium text-foreground">{app.name}</div>
        <div className="truncate text-xs text-muted-foreground">/{app.slug}</div>
      </div>
      <div className="flex shrink-0 items-center gap-4">
        {/* Operator console → a native select is acceptable here (not a
            portal-facing surface). */}
        <select
          value={defaultRoleId}
          disabled={!detail || save.isPending}
          onChange={(e) => {
            const v = e.target.value;
            // Clearing the role must also clear auto-enroll (backend 422).
            save.mutate(v ? { default_role_id: v } : { default_role_id: null, auto_enroll: false });
          }}
          className="h-9 rounded-md border border-border bg-background px-2 text-sm text-foreground disabled:opacity-50"
        >
          <option value="">{t("portalSecurity.noDefaultRole", "No default role")}</option>
          {roles.map((r) => (
            <option key={r.id} value={r.id}>
              {r.name}
            </option>
          ))}
        </select>
        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground">
            {t("portalSecurity.autoEnroll", "Auto-access")}
          </span>
          <Switch
            checked={autoEnroll}
            disabled={!defaultRoleId || save.isPending}
            onCheckedChange={(v) => save.mutate({ auto_enroll: v })}
          />
        </div>
      </div>
    </div>
  );
}

export function PortalSecurityPage() {
  const { t } = useTranslation();
  const qc = useQueryClient();

  const regQ = useQuery({ queryKey: ["portal-registration"], queryFn: () => parentPortalApi.getRegistration() });
  const [draft, setDraft] = useState<Registration>({ enabled: false, mode: "direct" });
  useEffect(() => {
    if (regQ.data) setDraft(regQ.data);
  }, [regQ.data]);

  const saveReg = useMutation({
    mutationFn: (body: Registration) => parentPortalApi.updateRegistration(body),
    onSuccess: (saved) => {
      qc.setQueryData(["portal-registration"], saved);
      // The public brand's signup_enabled mirrors this — refresh portal reads.
      qc.invalidateQueries({ queryKey: ["parent-brand"] });
    },
  });

  const appsQ = useQuery({ queryKey: ["apps", "list"], queryFn: () => appsApi.list() });

  // 2FA enforcement policy (ADR-0017) — per realm. Optimistic + invalidate.
  const secQ = useQuery({ queryKey: ["instance-security"], queryFn: () => parentPortalApi.getSecurity() });
  const saveSec = useMutation({
    mutationFn: (body: Security) => parentPortalApi.updateSecurity(body),
    onSuccess: (saved) => qc.setQueryData(["instance-security"], saved),
  });
  const sec = secQ.data ?? { totp_required_app_users: false };

  const apps = appsQ.data ?? [];
  const dirty = !!regQ.data && JSON.stringify(draft) !== JSON.stringify(regQ.data);

  return (
    <PageContainer>
      <PageHeader
        title={t("portalSecurity.title", "Security")}
        description={t(
          "portalSecurity.subtitle",
          "Who can register for the portal and how new accounts are approved.",
        )}
      />
      <PageContent className="mx-auto max-w-3xl space-y-6">
        {/* Registration policy. */}
        <section className="rounded-xl border border-border bg-surface p-5">
          <div className="mb-2 flex items-center gap-2">
            <ShieldCheck className="h-4 w-4 text-muted-foreground" />
            <h2 className="text-sm font-semibold text-foreground">
              {t("portalSecurity.registration", "Registration")}
            </h2>
          </div>
          <div className="divide-y divide-border">
            <Row
              title={t("portalSecurity.enabled", "Allow self-signup")}
              help={t(
                "portalSecurity.enabledHelp",
                "When on, a 'create account' link appears on the portal login.",
              )}
            >
              <Switch
                checked={draft.enabled}
                onCheckedChange={(v) => setDraft((d) => ({ ...d, enabled: v }))}
              />
            </Row>
            <Row
              title={t("portalSecurity.mode", "New registrations")}
              help={t(
                "portalSecurity.modeHelp",
                "Direct: usable immediately. Approval: a Tray admin must accept the account first.",
              )}
            >
              <div
                className={`inline-flex rounded-lg border border-border p-0.5 ${
                  draft.enabled ? "" : "pointer-events-none opacity-50"
                }`}
              >
                {(["direct", "requires_approval"] as const).map((m) => (
                  <button
                    key={m}
                    type="button"
                    onClick={() => setDraft((d) => ({ ...d, mode: m }))}
                    className={`rounded-md px-3 py-1.5 text-xs font-medium transition ${
                      draft.mode === m
                        ? "bg-accent text-accent-foreground"
                        : "text-muted-foreground hover:text-foreground"
                    }`}
                  >
                    {m === "direct"
                      ? t("portalSecurity.modeDirect", "Direct")
                      : t("portalSecurity.modeApproval", "Approval")}
                  </button>
                ))}
              </div>
            </Row>
          </div>
          <div className="mt-4 flex items-center justify-end gap-3">
            {saveReg.isSuccess && !dirty && (
              <span className="flex items-center gap-1 text-xs text-success">
                <Check className="h-3.5 w-3.5" /> {t("common.saved", "Saved")}
              </span>
            )}
            <Button onClick={() => saveReg.mutate(draft)} disabled={!dirty || saveReg.isPending}>
              {saveReg.isPending && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}
              {t("common.save", "Save")}
            </Button>
          </div>
        </section>

        {/* 2FA enforcement policy (ADR-0017). */}
        <section className="rounded-xl border border-border bg-surface p-5">
          <div className="mb-2 flex items-center gap-2">
            <ShieldCheck className="h-4 w-4 text-muted-foreground" />
            <h2 className="text-sm font-semibold text-foreground">{t("twoFactor.policyTitle")}</h2>
          </div>
          <p className="mb-1 text-xs text-muted-foreground">{t("twoFactor.policyHelp")}</p>
          <div className="divide-y divide-border">
            <Row title={t("twoFactor.requireAppUsers")}>
              <Switch
                checked={sec.totp_required_app_users}
                disabled={saveSec.isPending || secQ.isPending}
                onCheckedChange={(v) => saveSec.mutate({ totp_required_app_users: v })}
              />
            </Row>
          </div>
        </section>

        {/* The registrations queue lives in its own sidebar entry + grid
            (PortalRegistrationsPage) — it's an operational worklist, not a
            setting. */}

        {/* Per-app access defaults: default role + auto-access. */}
        <section className="rounded-xl border border-border bg-surface p-5">
          <div className="mb-1 flex items-center gap-2">
            <AppWindow className="h-4 w-4 text-muted-foreground" />
            <h2 className="text-sm font-semibold text-foreground">
              {t("portalSecurity.appAccess", "App access")}
            </h2>
          </div>
          <p className="mb-3 text-xs text-muted-foreground">
            {t(
              "portalSecurity.appAccessHelp",
              "The role pre-selected when assigning an app. With auto-access on, every accepted user is enrolled into that app automatically (requires a default role).",
            )}
          </p>

          {appsQ.isPending ? (
            <div className="flex justify-center py-8 text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin" />
            </div>
          ) : apps.length === 0 ? (
            <EmptyState title={t("portalSecurity.noApps", "No apps yet")} />
          ) : (
            <div className="divide-y divide-border">
              {apps.map((a) => (
                <AppAccessRow key={a.id} app={a} />
              ))}
            </div>
          )}
        </section>
      </PageContent>
    </PageContainer>
  );
}
